import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { CheckBrowserChartService } from '@app/core/services/check-browser-chart.service';
import { IChartSettings } from '@app/core/interfaces/chart-settings';

@Component({
    selector: 'app-series-chart',
    templateUrl: './series-chart.component.html',
    styleUrls: ['./series-chart.component.scss']
})
export class SeriesChartComponent implements OnChanges, AfterViewInit {
    @Input() public chartSettings: IChartSettings = {
        name: 'chartDefault',
        chartData: [{ nameSeries: '', data: [''], color: 0x00cc99, gradient: false }],
        cursor: true,
        legend: true,
        tooltip: true
    };
    private root!: am5.Root;
    private chart!: am5xy.XYChart;
    private xAxis!: any;
    private yAxis!: any;
    private series!: am5xy.LineSeries;

    public constructor(private checkBrowserOnly: CheckBrowserChartService, private changeDetectorRef: ChangeDetectorRef) {}
    public ngAfterViewInit(): void {
        this.root = am5.Root.new(this.chartSettings.name);
    }

    public ngOnChanges(): void {
        if (this.chartSettings.chartData.length === 0) {
            return;
        }

        this.checkBrowserOnly.browserOnly(() => {
            if (this.root === undefined) {
                return;
            }
            this.root.container.children.clear();
            this.root.setThemes([am5themes_Animated.new(this.root)]);
            this.setChart();
            this.setXAxis();
            this.setYAxis();
            this.setCursor();
            this.setTooltip();
            this.chartSettings.chartData.forEach((element) => {
                this.createSeries(element.nameSeries, 'value', element.data, element.color, element.gradient);
            });
            this.changeDetectorRef.markForCheck();
        });
    }

    private setChart(): void {
        this.chart = this.root.container.children.push(
            am5xy.XYChart.new(this.root, {
                panX: false,
                panY: false,
                wheelX: 'panX',
                wheelY: 'panY',
                layout: this.root.verticalLayout,
                maxTooltipDistance: 0
            })
        );
    }

    private setXAxis(): void {
        this.xAxis = this.chart.xAxes.push(
            am5xy.GaplessDateAxis.new(this.root, {
                maxDeviation: 0,
                baseInterval: {
                    timeUnit: 'day',
                    count: 1
                },
                renderer: am5xy.AxisRendererX.new(this.root, {}),
                tooltip: am5.Tooltip.new(this.root, {})
            })
        );
    }

    private setYAxis(): void {
        this.yAxis = this.chart.yAxes.push(
            am5xy.ValueAxis.new(this.root, {
                extraMin: 0.2,
                extraTooltipPrecision: 1,
                renderer: am5xy.AxisRendererY.new(this.root, {})
            })
        );
    }

    private setCursor(): void {
        if (this.chartSettings.cursor) {
            this.chart.set(
                'cursor',
                am5xy.XYCursor.new(this.root, {
                    behavior: 'none',
                    xAxis: this.xAxis
                })
            );
        }
    }

    private setTooltip(): void {
        if (this.chartSettings.tooltip) {
            this.xAxis.set(
                'tooltip',
                am5.Tooltip.new(this.root, {
                    themeTags: ['axis']
                })
            );

            this.yAxis.set(
                'tooltip',
                am5.Tooltip.new(this.root, {
                    themeTags: ['axis']
                })
            );
        }
    }

    private createSeries(name: string, field: string, data: Array<any>, color: any, gradient: boolean): void {
        this.series = this.chart.series.push(
            am5xy.LineSeries.new(this.root, {
                name: name,
                xAxis: this.xAxis,
                yAxis: this.yAxis,
                valueYField: field,
                valueXField: 'date',
                stroke: am5.color(color)
            })
        );

        if (gradient) {
            this.setGradient(color);
        }

        if (this.chartSettings.legend) {
            let legend: am5.Legend = this.chart.children.push(am5.Legend.new(this.root, { y: am5.p0, x: am5.p50 }));
            legend.data.setAll(this.chart.series.values);
        }

        this.series.data.setAll(data);
    }

    private setGradient(color: any): void {
        this.series.fills.template.set(
            'fillGradient',
            am5.LinearGradient.new(this.root, {
                stops: [
                    {
                        color: am5.color(color),
                        opacity: 0.8
                    },
                    {
                        color: am5.color(0xd9d9d9),
                        opacity: 0.3
                    }
                ]
            })
        );

        this.series.fills.template.setAll({
            visible: true,
            fillOpacity: 1
        });

        this.series.strokes.template.setAll({
            strokeWidth: 2
        });
    }
}
